import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import OrderListPage from './pages/OrderListPage';
import PickingPage from './pages/PickingPage';
import MultiPickingPage from './pages/MultiPickingPage';
import HoldItemsPage from './pages/HoldItemsPage';
import FinalizePage from './pages/FinalizePage';
import AdminDashboard from './pages/AdminDashboard';
import SlaMonitorPage from './pages/SlaMonitorPage';
import ExecutiveOverviewPage from './pages/ExecutiveOverviewPage';
import LoadingOverlay from './components/LoadingOverlay';
import AppNav from './components/AppNav';
import {
  fetchAndStoreUserRole,
  getAuthHeaders,
  grantConfiguredAdminAccessIfMatch,
  hasVerifiedAdminPanelAccess,
  isAuthed,
  logout,
  verifyAdminAccessWithToken,
  verifyAdminPanelAccess,
} from './utils/auth';
import { apiFetch } from './utils/api';
import { initPickingSync } from './utils/syncState';

const isAdministratorRole = () => {
  const roleId = localStorage.getItem('roleId');
  const roleKey = (localStorage.getItem('roleNameKey') || '').toLowerCase();
  if (roleId && Number(roleId) === 1) return true;
  return roleKey === 'administrators' || hasVerifiedAdminPanelAccess();
};

const AdminRouteGate = ({ isAdmin, children }) => {
  const navigate = useNavigate();
  const [isVerified, setIsVerified] = React.useState(() => isAdmin || hasVerifiedAdminPanelAccess());
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    setIsVerified(isAdmin || hasVerifiedAdminPanelAccess());
  }, [isAdmin]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await verifyAdminPanelAccess(password);
      setPassword('');
      setIsVerified(true);
    } catch (err) {
      setError(err.message || 'Access denied.');
    } finally {
      setBusy(false);
    }
  };

  if (isVerified) return children;

  return (
    <div className="operator-gate">
      <div className="operator-gate-inner">
        <h1 className="operator-gate-title">Admin Area</h1>
        <div className="operator-gate-card">
          <button
            type="button"
            className="operator-gate-back"
            onClick={() => navigate('/order-list')}
          >
            ← Back to Orders
          </button>
          <div className="operator-gate-icon">Restricted</div>
          <p>Confirm your Magento password. Access requires admin permission.</p>
          <form onSubmit={handleSubmit}>
            <label htmlFor="adminPanelPassword">Password</label>
            <input
              id="adminPanelPassword"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoFocus
            />
            {error && <span className="operator-gate-error">{error}</span>}
            <button type="submit" disabled={busy}>
              {busy ? 'Verifying...' : 'Verify access'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const ChromeNav = ({ isUserAuthenticated, isAdmin }) => {
  const location = useLocation();
  if (!isUserAuthenticated) return null;
  if (location.pathname === '/') return null;
  return <AppNav isAdmin={isAdmin} />;
};

function App() {
  const sessionExists = isAuthed();
  const [isUserAuthenticated, setIsUserAuthenticated] = React.useState(sessionExists);
  const [authReady, setAuthReady] = React.useState(!sessionExists);
  const [isAdmin, setIsAdmin] = React.useState(() => isAdministratorRole());

  React.useEffect(() => {
    const verifyToken = async () => {
      if (!isAuthed()) {
        setIsUserAuthenticated(false);
        setAuthReady(true);
        return;
      }
      try {
        const res = await apiFetch('/rest/V1/users/me', {
          headers: getAuthHeaders(),
        });
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            throw new Error('unauthorized');
          }
          console.warn('users/me verification returned non-fatal status:', res.status);
          setIsUserAuthenticated(true);
        } else {
          setIsUserAuthenticated(true);
        }
      } catch (err) {
        if (String(err?.message).toLowerCase().includes('unauthorized')) {
          console.warn('Token verification failed. Clearing session.', err);
          logout();
          setIsUserAuthenticated(false);
        } else {
          console.warn('Token verification encountered a recoverable issue:', err);
          setIsUserAuthenticated(true);
        }
      } finally {
        setAuthReady(true);
      }
    };

    verifyToken();

    const handleAuthChange = () => {
      const authed = isAuthed();
      setIsUserAuthenticated(authed);
      setIsAdmin(isAdministratorRole());
      if (!authed) {
        setAuthReady(true);
      }
    };

    window.addEventListener('storage', handleAuthChange);
    window.addEventListener('auth-changed', handleAuthChange);
    window.addEventListener('user-meta-updated', handleAuthChange);
    window.addEventListener('admin-access-verified', handleAuthChange);
    return () => {
      window.removeEventListener('storage', handleAuthChange);
      window.removeEventListener('auth-changed', handleAuthChange);
      window.removeEventListener('user-meta-updated', handleAuthChange);
      window.removeEventListener('admin-access-verified', handleAuthChange);
    };
  }, []);

  // Sincroniza o progresso do picking entre dispositivos (tablet/PC). Degrada
  // graciosamente se o endpoint state.php ainda nao estiver publicado.
  React.useEffect(() => {
    if (isUserAuthenticated) {
      initPickingSync();
    }
  }, [isUserAuthenticated]);

  React.useEffect(() => {
    const ensureRole = async () => {
      if (!isUserAuthenticated) return;
      const existingRoleKey = (localStorage.getItem('roleNameKey') || '').toLowerCase();
      const existingRoleId = localStorage.getItem('roleId');
      const checkState = localStorage.getItem('roleCheck');
      if ((existingRoleKey || existingRoleId) || checkState === 'none') return;
      await fetchAndStoreUserRole();
      setIsAdmin(isAdministratorRole());
    };
    ensureRole();
  }, [isUserAuthenticated]);

  // Revalida o acesso de admin no boot (nao apenas no login). Sem isto, o status
  // de admin vive so no sessionStorage gravado durante o login e some ao reabrir
  // a aba — fazendo os botoes de admin desaparecerem.
  React.useEffect(() => {
    const ensureAdminAccess = async () => {
      if (!isUserAuthenticated) return;
      // 1) Reconhecimento direto e duravel via lista de admins configurados
      //    (sincrono, nao depende de /rest/V1/users/* que retorna 404 aqui).
      if (grantConfiguredAdminAccessIfMatch()) {
        setIsAdmin(true);
        return;
      }
      // 2) Ja reconhecido (role no localStorage ou sessionStorage da sessao atual).
      if (isAdministratorRole()) {
        setIsAdmin(true);
        return;
      }
      // 3) Fallback: tenta confirmar via API (pode 404 nesta instalacao; no-op se falhar).
      const operator = localStorage.getItem('operator') || localStorage.getItem('username') || '';
      if (!isAuthed() || !operator) return;
      try {
        await verifyAdminAccessWithToken(operator);
        setIsAdmin(isAdministratorRole());
      } catch (err) {
        console.debug('Admin access re-check failed:', err);
      }
    };
    ensureAdminAccess();
  }, [isUserAuthenticated]);

  if (!authReady) {
    return (
      <div className="app-loader">
        <LoadingOverlay show ariaLabel="Authenticating session" />
      </div>
    );
  }

  return (
    <Router>
      <ChromeNav isUserAuthenticated={isUserAuthenticated} isAdmin={isAdmin} />
      <Routes>
        <Route path="/" element={isUserAuthenticated ? <Navigate to="/order-list" /> : <LoginPage />} />
        <Route
          path="/order-list"
          element={isUserAuthenticated ? <OrderListPage isAdmin={isAdmin} /> : <Navigate to="/" />}
        />
        <Route path="/picking" element={isUserAuthenticated ? <PickingPage /> : <Navigate to="/" />} />
        <Route path="/picking/:orderId" element={isUserAuthenticated ? <PickingPage /> : <Navigate to="/" />} />
        <Route path="/multi-picking" element={isUserAuthenticated ? <MultiPickingPage /> : <Navigate to="/" />} />
        <Route path="/on-hold" element={isUserAuthenticated ? <HoldItemsPage /> : <Navigate to="/" />} />
        <Route path="/finalize/:incrementId" element={isUserAuthenticated ? <FinalizePage /> : <Navigate to="/" />} />
        <Route
          path="/admin/operators"
          element={
            isUserAuthenticated ? (
              <AdminRouteGate isAdmin={isAdmin}>
                <AdminDashboard />
              </AdminRouteGate>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/admin/sla"
          element={
            isUserAuthenticated ? (
              <AdminRouteGate isAdmin={isAdmin}>
                <SlaMonitorPage />
              </AdminRouteGate>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/admin/overview"
          element={
            isUserAuthenticated ? (
              <AdminRouteGate isAdmin={isAdmin}>
                <ExecutiveOverviewPage />
              </AdminRouteGate>
            ) : (
              <Navigate to="/" />
            )
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
